/**
 *
 * "Generates Recent Maintenance Jobs box with maintanenance reports for viewing and selecting
 * to generate post shut report."
 *
 *
 * @file   MaintenanceJobs.js
 * @author Lateral
 * @since  2023
 */

import { Box, Checkbox, Stack, Typography } from '@mui/material'
import React from 'react'
import { Controller } from 'react-hook-form'
import { useTheme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { recentJobsName } from 'pages/reporting/common/formNames'
import DeckModelReport from 'components/mobileDeckCanvas/components/DeckModel/DeckModelReport'
import { DeckMode } from 'common/deckMode'
import { dateFormat } from 'common/dates'

function MaintenanceJobs({ deckRevisions, deckRevisionHistories, control, fields }) {
  /**
   * Component for viewing and selecting Maintenances for a Post Shut Report.
   * Also renders hidden canvases that are revealed during the rendering process."
   *
   * @function
   *
   * @param {object[]} deckRevisions - array of deck revisions
   * @param {object[]} deckRevisionHistories - array of deck maintenance objects
   * @param {object} control - Register object
   * @param {object[]} fields - Array of post shut report filds to match with deck revisions
   * @param {number} isVisible - Number of visible revisions
   * @param {string} watchSite - Selected site ID
   *
   * @returns {object} - A box component for viewing and selecting Maintenances
   */
  const theme = useTheme()

  return (
    <Box
      sx={{
        padding: '1em 1em 2em 1em',
        marginTop: '0.5em'
      }}>
      {fields.length > 0 ? (
        <Stack
          sx={{
            overflow: 'scroll'
          }}
          spacing={3}>
          {fields.map((f, index) => {
            const deckRevisionHistory = deckRevisionHistories.find((d) => d.id === f.HistoryId)
            const deckRevision = deckRevisions.find((d) => d.id === deckRevisionHistory.RevisionId)
            return (
              <Stack
                key={f.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ borderBottom: `1px solid ${theme.palette.primary.contrastText}` }}>
                <Controller
                  control={control}
                  name={`${recentJobsName}.${index}.Include`}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Checkbox
                      checked={value}
                      size="small"
                      onChange={onChange}
                      sx={{ color: `${theme.palette.supporting.highlight}` }}
                      {...field}
                    />
                  )}
                />

                <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography sx={{ float: 'left' }}>{f.Name}</Typography>
                  <Typography sx={{ float: 'right', opacity: '50%' }}>{`Date Performed: ${dayjs(f.DatePerformed).format(
                    dateFormat
                  )}`}</Typography>
                </Stack>

                <Box id={`${f.HistoryId}`} sx={{ padding: '1em' }}>
                  <DeckModelReport
                    data={deckRevision}
                    maintenance={deckRevisionHistory}
                    mode={DeckMode.postMaintenance}
                    selected={[]}
                    presentation
                  />
                </Box>

                <Box id={`${f.HistoryId}-pre`} sx={{ padding: '1em' }}>
                  <DeckModelReport
                    data={deckRevision}
                    maintenance={deckRevisionHistory}
                    mode={DeckMode.preMaintenance}
                    selected={[]}
                    presentation
                  />
                </Box>

                <Box id={`${f.HistoryId}-default`} sx={{ padding: '1em' }} hidden>
                  <DeckModelReport data={deckRevision} mode={DeckMode.default} selected={[]} presentation />
                </Box>
              </Stack>
            )
          })}
        </Stack>
      ) : (
        <Typography>No maintenance to show</Typography>
      )}
    </Box>
  )
}
export default MaintenanceJobs
